/**
 * accessCodeClient — the one client-side entry point for the access-code
 * endpoints: redeeming a code (redeemAccessCode) and the pre-auth status
 * probe (checkAccessCode).
 *
 * Extracted 2026-08-14 when the top-up popup gained promo-code redemption:
 * StartTrial.tsx and Pricing.tsx each carried their own byte-identical fetch
 * against redeemAccessCode, and a third copy inside the modal would have been
 * the drift the consolidation rules forbid. Unified 2026-08-16 (LP-R4): both
 * pages now route redeem AND check through this module, so the mapped copy
 * below — including CODE_EXPIRED / CODE_AMOUNT_INVALID — is reachable from
 * every surface instead of each page's partial inline error map.
 *
 * Promo codes (PR0-…, kind 'wallet_credit') credit the account's PAYING
 * wallet with the exact amount minted onto the code, whatever the redeemer's
 * subscription posture. Trial codes keep their existing behavior — the server
 * classifies the mode; this module never decides it.
 */

import { getAuth } from 'firebase/auth';

export type RedemptionMode =
  | 'wallet_credit'
  | 'trial_extension'
  | 'trial_start'
  | 'returning';

/** A code's kind, as reported by checkAccessCode (LP-R4 honest narrative). */
export type AccessCodeKind = 'trial' | 'wallet_credit';

export interface RedeemCodeResult {
  success: true;
  mode: RedemptionMode;
  /** True when this user already activated the same code (server `returning`). */
  returning: boolean;
  /** wallet_credit / trial_start / returning: the credit amount. */
  creditAmountCents?: number;
  /** trial_extension: the amount ADDED to the remaining balance. */
  creditAddedCents?: number;
  /** trial_extension: the remaining balance after the add. */
  creditRemainingCents?: number;
  /** trial_start / trial_extension: ISO string of the (new) trial end date. */
  expiresAt?: string | null;
  tokensCredited?: number;
  creditedWallet?: string;
  /** trial_start only: whether the dedicated VM provisioning kicked off. */
  vmProvisioned?: boolean;
}

export interface RedeemCodeFailure {
  success: false;
  /** Server error token (CODE_NOT_FOUND, CODE_EXPIRED, …) when one was returned. */
  code: string | null;
  /** Ready-to-render, lowercase message in the product's voice. */
  message: string;
  /**
   * CODE_REDEEMED_BY_OTHER only: masked email of the account the code is
   * linked to ("m****@gmail.com"). Served exclusively on this AUTHENTICATED
   * failure — the pre-auth checkAccessCode probe never discloses it (LP-R5).
   */
  emailHint?: string | null;
}

export interface CheckCodeResult {
  success: true;
  status: 'unused' | 'redeemed';
  /** unused only: the code's trial tier ('a' | 'b'). */
  tier?: string;
  /** The code's kind — lets a surface narrate a promo honestly (LP-R4). */
  kind: AccessCodeKind;
  /** redeemed only: the code is attached to an existing account (no email disclosed pre-auth). */
  attachedToExistingAccount: boolean;
}

export interface CheckCodeFailure {
  success: false;
  /** Server error token (CODE_NOT_FOUND, CODE_EXPIRED, RATE_LIMITED, …) when one was returned. */
  code: string | null;
  /** Ready-to-render, lowercase message in the product's voice. */
  message: string;
}

function getProjectId(): string {
  return process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
}

function fnUrl(name: string): string {
  return `https://us-central1-${getProjectId()}.cloudfunctions.net/${name}`;
}

/**
 * Human copy for every failure the access-code endpoints can return.
 * `emailHint` (CODE_REDEEMED_BY_OTHER, authenticated redeem only) folds the
 * masked account into the sign-in guidance.
 */
export function messageForRedeemError(
  errorCode: string | null | undefined,
  emailHint?: string | null,
): string {
  switch (errorCode) {
    case 'CODE_NOT_FOUND':
      return "that code isn't recognized — check the characters and try again.";
    case 'CODE_REDEEMED_BY_OTHER':
      return `that code is already linked to a different account${emailHint ? ` (${emailHint})` : ''} — sign in with the account that originally activated it.`;
    case 'CODE_EXPIRED':
      return 'that code has expired.';
    case 'CODE_ALREADY_STARTED':
      return 'that start is for a brand-new account, and yours has already started — it keeps working as it is.';
    case 'CODE_AMOUNT_INVALID':
      return "that code couldn't be applied — please contact your prest0 point of contact.";
    case 'TOKEN_PRICE_UNAVAILABLE':
      return 'we could not price the credit just now — please try again in a moment.';
    case 'RATE_LIMITED':
      // Both endpoints rate-limit on a one-hour IP window — say so.
      return 'too many attempts — please try again in an hour.';
    default:
      return 'we could not apply that code — please try again.';
  }
}

/**
 * Redeem a code for the signed-in user. Never throws: every failure comes
 * back as a typed result so callers can render it inline.
 */
export async function redeemAccessCode(rawCode: string): Promise<RedeemCodeResult | RedeemCodeFailure> {
  const code = (rawCode || '').trim().toUpperCase();
  if (!code) {
    return { success: false, code: null, message: 'enter a code first.' };
  }

  let token: string | null = null;
  try {
    token = (await getAuth().currentUser?.getIdToken()) ?? null;
  } catch {
    token = null;
  }
  if (!token) {
    return { success: false, code: null, message: 'please sign in again to redeem a code.' };
  }

  try {
    const resp = await fetch(fnUrl('redeemAccessCode'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const data = await resp.json().catch(() => ({} as Record<string, unknown>));
    if (!resp.ok) {
      const errorCode = typeof (data as any)?.error === 'string' ? (data as any).error : null;
      const emailHint = typeof (data as any)?.emailHint === 'string' ? (data as any).emailHint : null;
      return {
        success: false,
        code: errorCode,
        message: messageForRedeemError(errorCode, emailHint),
        ...(emailHint ? { emailHint } : {}),
      };
    }
    return {
      success: true,
      mode: ((data as any)?.mode as RedemptionMode) || 'wallet_credit',
      returning: (data as any)?.returning === true,
      creditAmountCents: (data as any)?.creditAmountCents,
      creditAddedCents: (data as any)?.creditAddedCents,
      creditRemainingCents: (data as any)?.creditRemainingCents,
      expiresAt: typeof (data as any)?.expiresAt === 'string' ? (data as any).expiresAt : null,
      tokensCredited: (data as any)?.tokensCredited,
      creditedWallet: (data as any)?.creditedWallet,
      vmProvisioned: (data as any)?.vmProvisioned === true,
    };
  } catch {
    return { success: false, code: null, message: 'network trouble — please try again.' };
  }
}

/**
 * Pre-auth status probe for a code — READ-ONLY (a shared link can never
 * consume a code). Never throws. The server pre-checks expiry for unused
 * codes (CODE_EXPIRED failure — a dead code must not funnel a signup) and,
 * for redeemed codes, discloses only the attachedToExistingAccount boolean:
 * the masked email hint exists solely on the authenticated redeem path.
 */
export async function checkAccessCode(rawCode: string): Promise<CheckCodeResult | CheckCodeFailure> {
  const code = (rawCode || '').trim().toUpperCase();
  if (!code) {
    return { success: false, code: null, message: 'enter a code first.' };
  }

  try {
    const resp = await fetch(fnUrl('checkAccessCode'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const data = await resp.json().catch(() => ({} as Record<string, unknown>));
    if (!resp.ok) {
      const errorCode = typeof (data as any)?.error === 'string' ? (data as any).error : null;
      return { success: false, code: errorCode, message: messageForRedeemError(errorCode) };
    }
    const status: 'unused' | 'redeemed' = (data as any)?.status === 'unused' ? 'unused' : 'redeemed';
    return {
      success: true,
      status,
      tier: typeof (data as any)?.tier === 'string' ? (data as any).tier : undefined,
      kind: (data as any)?.kind === 'wallet_credit' ? 'wallet_credit' : 'trial',
      attachedToExistingAccount: status === 'redeemed'
        ? (data as any)?.attachedToExistingAccount !== false
        : false,
    };
  } catch {
    return { success: false, code: null, message: 'network trouble — please try again.' };
  }
}

export default redeemAccessCode;
